import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../../configuration";
import { FetchError, GetMeetingNotesDto } from "../../../types";
import { useAccessToken } from "../../../use-access-token";
import { queryKeys } from "../../queryKeys";

type Variables = {
  meetingGuid: string;
  meetingNoteGuid: string;
  isPrivate: boolean;
};

const useDeleteMeetingNoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteMeetingNoteRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/meeting-notes/${variables.meetingNoteGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteMeetingNoteRequest };
};

const useDeleteMeetingNote = () => {
  const { deleteMeetingNoteRequest } = useDeleteMeetingNoteRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMeetingNoteRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(queryKeys.meetings.getMeetingNotes(variables.meetingGuid));

      const notesCacheKey = variables.isPrivate
        ? queryKeys.meetings.getPrivateMeetingNotes(variables.meetingGuid)
        : queryKeys.meetings.getSharedMeetingNotes(variables.meetingGuid);

      const cachedData = queryClient.getQueryData<GetMeetingNotesDto>(notesCacheKey);

      if (cachedData) {
        const optimisticallyUpdatedData: GetMeetingNotesDto = {
          ...cachedData,
          meetingNotes: cachedData.meetingNotes.filter((meetingNote) => meetingNote.guid !== variables.meetingNoteGuid),
        };

        queryClient.setQueryData(notesCacheKey, optimisticallyUpdatedData);
      }

      return { cachedData, notesCacheKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.notesCacheKey) {
        queryClient.setQueryData(context.notesCacheKey, context.cachedData);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingNotes(variables.meetingGuid));
    },
  });
};

export { useDeleteMeetingNote };
